/**
 * Гостевой просмотр.
 *
 * Режим «показать, как видит наблюдатель»: интерфейс скрывает всё, чего нет
 * у роли VIEWER, чтобы владелец мог проверить права глазами, а не по таблице
 * в документации.
 *
 * Важное ограничение, которое нельзя обойти молчанием: это ограничение
 * **интерфейса**, а не доступа. Сервер по-прежнему знает настоящую роль и
 * выполнит любой запрос, на который она даёт право. Режим отвечает на вопрос
 * «что увидит наблюдатель», а не «что я смогу сделать» — и в интерфейсе это
 * сказано прямо, чтобы им не пытались подменить проверку безопасности.
 *
 * Состояние живёт в памяти вкладки и сбрасывается при перезагрузке: режим
 * включают на минуту для проверки, и застрять в нём случайно нельзя.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ProjectPermission } from '@project-os/contracts';
import { ROLE_PERMISSIONS } from '@project-os/contracts';

interface ViewerModeValue {
  /** Включён ли гостевой просмотр. */
  active: boolean;
  toggle: () => void;
  /**
   * Права с учётом режима: в гостевом просмотре остаются только те,
   * что есть у наблюдателя.
   */
  filter: (permissions: ProjectPermission[]) => ProjectPermission[];
}

const ViewerModeContext = createContext<ViewerModeValue | null>(null);

export function ViewerModeProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState(false);

  const value = useMemo<ViewerModeValue>(() => {
    const viewerPermissions = new Set<string>(ROLE_PERMISSIONS.VIEWER);

    return {
      active,
      toggle: () => setActive((previous) => !previous),
      filter: (permissions) =>
        active
          ? permissions.filter((permission) => viewerPermissions.has(permission))
          : permissions,
    };
  }, [active]);

  return <ViewerModeContext.Provider value={value}>{children}</ViewerModeContext.Provider>;
}

/**
 * Гостевой просмотр.
 *
 * Вне провайдера режим выключен: компонент, вырванный в изолированный
 * рендер, должен показывать обычный интерфейс, а не пустой экран.
 */
export function useViewerMode(): ViewerModeValue {
  return (
    useContext(ViewerModeContext) ?? {
      active: false,
      toggle: () => {},
      filter: (permissions) => permissions,
    }
  );
}
